import type { OperationVariables, UseFragmentOptions } from '@apollo/client';
import type { EventId } from '@ps/isp-web-services-types';
import {
  AmericanFootballFragmentDoc,
  BaseballFragmentDoc,
  BasketballFragmentDoc,
  DartsFragmentDoc,
  ESportsFragmentDoc,
  FootballFragmentDoc,
  HandballFragmentDoc,
  IceHockeyFragmentDoc,
  SnookerFragmentDoc,
  TableTennisFragmentDoc,
  TennisFragmentDoc,
  VolleyballFragmentDoc
} from '@ps/web-shared-generated-graphql/sports';
import type { Required as Require } from 'utility-types';

import type { LiveDataStateVariables } from '../providers/LiveDataProvider';
import { EventTypeId } from '../types/eventType';

import type { LiveData } from './getLiveData';

/**
 * "Require" football to avoid it possibly being "undefined".
 * "Partial" to avoid including all event types.
 */
type EventTypeLiveDataFragmentMap = Require<
  Partial<
    Record<
      EventTypeId,
      (
        eventId: EventId,
        variables: LiveDataStateVariables
      ) => UseFragmentOptions<LiveData, OperationVariables>
    >
  >,
  EventTypeId.SOCCER
>;

const eventTypeLiveDataFragmentMap: EventTypeLiveDataFragmentMap = {
  [EventTypeId.AMERICAN_FOOTBALL]: (eventId, variables) => ({
    fragment: AmericanFootballFragmentDoc,
    fragmentName: 'AmericanFootball',
    from: {
      __typename: 'AmericanFootballFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.BASEBALL]: (eventId, variables) => ({
    fragment: BaseballFragmentDoc,
    fragmentName: 'Baseball',
    from: {
      __typename: 'BaseballFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.BASKETBALL]: (eventId, variables) => ({
    fragment: BasketballFragmentDoc,
    fragmentName: 'Basketball',
    from: {
      __typename: 'BasketballFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.DARTS]: (eventId, variables) => ({
    fragment: DartsFragmentDoc,
    fragmentName: 'Darts',
    from: {
      __typename: 'DartsFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.E_SPORTS]: (eventId, variables) => ({
    fragment: ESportsFragmentDoc,
    fragmentName: 'ESports',
    from: {
      __typename: 'EsportsFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.HANDBALL]: (eventId, variables) => ({
    fragment: HandballFragmentDoc,
    fragmentName: 'Handball',
    from: {
      __typename: 'HandballFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.ICE_HOCKEY]: (eventId, variables) => ({
    fragment: IceHockeyFragmentDoc,
    fragmentName: 'IceHockey',
    from: {
      __typename: 'IceHockeyFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.SOCCER]: (eventId, variables) => ({
    fragment: FootballFragmentDoc,
    fragmentName: 'Football',
    from: {
      __typename: 'FootballFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.SNOOKER]: (eventId, variables) => ({
    fragment: SnookerFragmentDoc,
    fragmentName: 'Snooker',
    from: {
      __typename: 'SnookerFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.TABLE_TENNIS]: (eventId, variables) => ({
    fragment: TableTennisFragmentDoc,
    fragmentName: 'TableTennis',
    from: {
      __typename: 'TableTennisFixture',
      id: eventId
    },
    variables
  }),
  [EventTypeId.TENNIS]: (eventId, variables) => ({
    fragment: TennisFragmentDoc,
    fragmentName: 'Tennis',
    from: {
      __typename: 'TennisMatch',
      id: eventId
    },
    variables
  }),
  [EventTypeId.VOLLEYBALL]: (eventId, variables) => ({
    fragment: VolleyballFragmentDoc,
    fragmentName: 'Volleyball',
    from: {
      __typename: 'VolleyballFixture',
      id: eventId
    },
    variables
  })
};

export const getEventScoreboardFragment = (
  eventTypeId: EventTypeId,
  eventId: EventId,
  variables: LiveDataStateVariables
) =>
  eventTypeLiveDataFragmentMap[eventTypeId]?.(eventId, variables) ??
  eventTypeLiveDataFragmentMap[EventTypeId.SOCCER](0, variables);
